import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from 'src/api/api';
import useAuth from 'src/hooks/useAuth';
import useLead from 'src/hooks/useLead';
import useAppointment from 'src/hooks/useAppointment';
import useDPXConversations from 'src/hooks/useDPXConversations';
import { ILead } from 'src/interfaces/lead';
import { IUser } from 'src/interfaces/user';
import { makeCall } from 'src/shared/communications';
import { resolveLeadChannel } from 'src/modules/assistant/helpers/resolveLeadChannel';
import { ACTION_ERROR } from 'src/modules/assistant/constants/actions';
import { actionFailure } from 'src/modules/assistant/actions/createActionHandler';
import { getActionHandler } from 'src/modules/assistant/actions/registry';
import {
    sendChannelMessage,
    sendChannelTemplate,
    TLeadChannelConversation,
} from 'src/modules/conversations/api';
import { uploadLocalFileForMessage } from 'src/views/OmniChannel/utils/outboundMultimedia';
import { useAssistantContext } from './useAssistantContext';
import {
    TAppointmentActions,
    TConversationActions,
    TLeadActions,
} from 'src/modules/assistant/actions/types';
import { TActionResult } from 'src/modules/assistant/actions/types';
import { TActionDraft } from 'src/modules/assistant/types/message';

type TLeadContext = TLeadActions & {
    lead: ILead | null;
    callToken?: string | null;
};

interface UseActionExecutorParams {
    leadId: string | null;
    channelConversation?: TLeadChannelConversation;
}

/**
 * Executes assistant actions for the lead in the active chat and records them.
 * Resolves to { ok, recorded?, data?, error? } and never throws.
 */
export const useActionExecutor = ({
    leadId,
    channelConversation = null,
}: UseActionExecutorParams) => {
    const { t } = useTranslation();
    const { user } = useAuth() as { user: IUser };
    const {
        lead,
        addComment,
        callToken,
        createTaskV2,
        generateToken,
        makeAICall,
        sendDocumentationV2,
        sendTemplateV2,
    } = useLead() as TLeadContext;
    const { createAssistantAppointment } =
        useAppointment() as TAppointmentActions;
    const { sendMessage } = useDPXConversations() as TConversationActions;
    const { completeAssistantAction } = useAssistantContext();
    const controllersRef = useRef(new Set<AbortController>());

    useEffect(() => {
        const controllers = controllersRef.current;
        return () => {
            controllers.forEach((controller) => controller.abort());
            controllers.clear();
        };
    }, []);

    const cancel = useCallback(() => {
        controllersRef.current.forEach((controller) => controller.abort());
    }, []);

    const execute = async (
        draft: TActionDraft,
        requestId: string
    ): Promise<TActionResult> => {
        if (!leadId || lead?._id !== leadId)
            return actionFailure(ACTION_ERROR.LEAD_MISMATCH);
        if (!lead.agent) return actionFailure(ACTION_ERROR.MISSING_AGENT);
        const handler = getActionHandler(draft.type);
        if (!handler) return actionFailure(ACTION_ERROR.UNSUPPORTED_ACTION);

        const controller = new AbortController();
        controllersRef.current.add(controller);
        try {
            const result = await handler.execute(draft, {
                api,
                lead,
                user,
                t,
                makeCall,
                callToken,
                signal: controller.signal,
                channel: resolveLeadChannel(lead, channelConversation),
                leadActions: {
                    addComment,
                    createTaskV2,
                    generateToken,
                    makeAICall,
                    sendDocumentationV2,
                    sendTemplateV2,
                },
                appointmentActions: { createAssistantAppointment },
                conversationActions: { sendMessage },
                channelMessaging: {
                    conversation: channelConversation,
                    sendMessage: sendChannelMessage,
                    sendTemplate: sendChannelTemplate,
                    uploadMedia: uploadLocalFileForMessage,
                },
            });
            if (!result.ok) return result;

            const execution = await completeAssistantAction(requestId, draft);
            if (!execution)
                return { ok: true, recorded: false, data: result.data };
            return {
                ok: true,
                recorded: true,
                data: { ...execution, ...result.data },
            };
        } finally {
            controllersRef.current.delete(controller);
        }
    };

    return { execute, cancel };
};
